import ddt from '@wix/da-ddt';
import { reduxKey } from './constants';
import { ChatListSubscriptionStatus, } from './types';
import { ChatWindowMode } from '../ChatWindow/types';
import noop from '../../utils/noop';
import { ChatPanelTab } from '../ChatPanel/ChatPanelTabs/types';
import { createSelector } from 'reselect';
function getState(state) {
    if (!state[reduxKey]) {
        return {};
    }
    return state[reduxKey];
}
// Status
export var getChatServiceStatus = function (state) { return getState(state).chatServiceStatus; };
export var getChatServiceError = function (state) { return getState(state).chatServiceError; };
// Auth
export var getChatAuthToken = function (state) { return getState(state).chatAuthToken; };
export var getChatPageType = function (state) { return getState(state).chatPageType; };
export var getChatPanelOpen = function (state) { return getState(state).chatPanelOpen; };
export var getChatNewMessageArrived = function (state) {
    return getState(state).newMessageArrived;
};
export var getShowOverlayChatList = function (state) {
    return getState(state).showOverlayChatList || false;
};
export var getNewChatParticipants = function (state) {
    return getState(state).newChatWindowParticipants;
};
export var isChatReduxReady = function (state) { return !!state[reduxKey]; };
// Chat list data
export var getChatListSubscriptionStatus = function (state) {
    return getState(state).chatListSubscriptionStatus;
};
export var getChatListError = function (state) {
    return getState(state).chatListSubscriptionError;
};
export var getChatListFetchMore = function (state) { var _a, _b; return (_b = (_a = getState(state).chatListSubscription) === null || _a === void 0 ? void 0 : _a.fetchMore) !== null && _b !== void 0 ? _b : noop; };
export var getChatListCursor = function (state) { var _a; return (_a = getState(state)) === null || _a === void 0 ? void 0 : _a.chatsCursor; };
export var getChatListChats = function (state) { return getState(state).chats; };
export var getChatListStatus = function (state) { return getState(state).chatsStatus; };
export var getChatUnreadCount = function (state) { var _a; return ((_a = getState(state).globalUnreadCount) === null || _a === void 0 ? void 0 : _a.count) || 0; };
export var getChatListIsReady = function (state) {
    var chatListSubscriptionStatus = getChatListSubscriptionStatus(state);
    var chatListStatus = getChatListStatus(state);
    return (isChatReduxReady(state) &&
        chatListSubscriptionStatus === ChatListSubscriptionStatus.LISTENING &&
        (chatListStatus === null || chatListStatus === void 0 ? void 0 : chatListStatus.isFetchingRooms) === false);
};
export var isChatListUsingCache = function (state) { return getState(state).chatsUsingCache; };
export var getChatListActiveTab = function (state) {
    return getState(state).chatListTab || ChatPanelTab.GENERAL;
};
// Chat requests
export var getChatRequestsSubscriptionStatus = function (state) {
    return getState(state).chatRequestsSubscriptionStatus;
};
export var getChatRequestsSubscriptionError = function (state) {
    return getState(state).chatRequestsSubscriptionError;
};
export var getChatRequestsFetchMore = function (state) { var _a, _b; return (_b = (_a = getState(state).chatRequestsSubscription) === null || _a === void 0 ? void 0 : _a.fetchMore) !== null && _b !== void 0 ? _b : noop; };
export var getChatRequestsCursor = function (state) { var _a; return (_a = getState(state)) === null || _a === void 0 ? void 0 : _a.chatRequestsCursor; };
export var getChatRequests = function (state) { return getState(state).chatRequests; };
export var getChatRequestsStatus = function (state) {
    return getState(state).chatRequestsStatus;
};
export var isChatRequestsUsingCache = function (state) {
    return getState(state).chatRequestsUsingCache;
};
// Chatrooms
export var getOpenChatrooms = function (state) { return getState(state).chatrooms; };
export var getOpenChatroomsOrder = function (state) {
    return getState(state).openChatroomsOrder;
};
/** Pulls a full list of participants we need to send a new chat request. This includes the DM participant's ID. */
export var getChatroomNewGroupChatParticipants = function (state, chatroomId) {
    var _a, _b, _c, _d, _e, _f;
    var participants = (_c = (_b = (_a = getOpenChatrooms(state)) === null || _a === void 0 ? void 0 : _a[chatroomId]) === null || _b === void 0 ? void 0 : _b.newChatParticipants) !== null && _c !== void 0 ? _c : [];
    var currentParticipant = (_f = (_e = (_d = getOpenChatrooms(state)) === null || _d === void 0 ? void 0 : _d[chatroomId]) === null || _e === void 0 ? void 0 : _e.room) === null || _f === void 0 ? void 0 : _f.otherParticipantId;
    if (currentParticipant) {
        participants.push({ id: currentParticipant, name: '' });
    }
    return participants;
};
export var getIsChatroomOpen = function (state, chatroomId) {
    var openChatroomIds = getOpenChatroomsOrder(state);
    return !!(openChatroomIds === null || openChatroomIds === void 0 ? void 0 : openChatroomIds.includes(chatroomId));
};
export var getNewChatRequests = function (state) { return getState(state).newChatRequests; };
export var getNewChatRequestsList = createSelector(getNewChatRequests, function (items) { return Object.values(items !== null && items !== void 0 ? items : {}); });
export var getPendingNewChatMessage = function (state, requestId) { var _a, _b; return (_b = (_a = getState(state).newChatRequests) === null || _a === void 0 ? void 0 : _a[requestId]) === null || _b === void 0 ? void 0 : _b.pendingMessage; };
var defaultChatroomValue = {};
export var getChatroom = function (state, chatId) {
    var chatrooms = getState(state).chatrooms;
    if (chatrooms && chatrooms.hasOwnProperty(chatId)) {
        return chatrooms[chatId];
    }
    else {
        ddt.log('chat', "We tried to get a chatroom that wasn't inside of our store -- chatroom id [".concat(chatId, "]"));
        return defaultChatroomValue;
    }
};
/** Return a list of chatroom ids of open chat windows that are currently minimized */
export var getMinimizedChatWindows = function (state) {
    var chatrooms = getOpenChatrooms(state) || {};
    var openChatroomsOrder = getOpenChatroomsOrder(state) || [];
    return Object.keys(chatrooms).filter(function (id) {
        var chatroom = chatrooms[id];
        // because a closed room's data persists in `chatrooms` even after it's closed,
        // we also need to check this array to see if it's been closed
        var isOpen = openChatroomsOrder.includes(id);
        return chatroom && isOpen && chatroom.mode === ChatWindowMode.MINIMIZED;
    });
};
export var getChatsWithNewMsgs = function (state) { return getState(state).chatsWithNewMsgs; };
export var getChatWindowFooterHeight = function (state, chatId) { var _a; return (_a = getState(state).footerHeights) === null || _a === void 0 ? void 0 : _a[chatId]; };
export var getNextShouldOpenRequestIds = function (state) { var _a; return (_a = getState(state).nextShouldOpenRequestIds) !== null && _a !== void 0 ? _a : []; };
export var getChatEnvironment = function (state) { var _a; return (_a = state.environment) === null || _a === void 0 ? void 0 : _a.type; };
export var getChatWorker = function (state) { return getState(state).chatWorker; };
export var getChatWorkerStatus = function (state) { return getState(state).chatWorkerStatus; };
export var getChatWorkerIdleStatus = function (state) {
    return getState(state).chatWorkerIdleStatus;
};
//# sourceMappingURL=selectors.js.map